import type { ComputedRef, Ref } from '@vue/composition-api';
import { ref, computed, onMounted, onUnmounted, reactive, watch } from '@vue/composition-api';
import { storeToRefs } from 'pinia';
import type { SectionTrackingPayload } from '@getaccept/lib-shared/src/tracking/types/section-tracking-payload';
import { TrackingType } from '@getaccept/lib-shared/src/tracking/types/tracking-type';
import type { NodeTrackingPayload } from '@getaccept/lib-shared/src/tracking/types/node-tracking-payload';
import { useTrackingStore } from '../../../tracking/store/tracking.store';
import { TrackingService } from '../../../../api/tracking/service/tracking.service';
import type { NodeTracking, NodeVisibilityPayload } from '../types/node-tracking';
import type { SectionTracking, SectionVisibilityPayload } from '../types/section-tracking';
import { NodeTrackingHelper } from '../helpers/node-tracking.helper';
import { useTimer } from './timer.composable';

export function useTracking() {
  const trackingStore = useTrackingStore();
  const { revision } = storeToRefs(trackingStore);
  const { ellapsedTime, stopTimer, startTimer } = useTimer();

  const nodeTrackings: NodeTracking[] = reactive([]);
  const nodeTrackingsPayload: ComputedRef<NodeTrackingPayload[]> = computed(() => {
    const nodeTrackingsWithTimeSpent: NodeTracking[] = nodeTrackings.filter(
      ({ timeSpent }: NodeTracking) => !!timeSpent
    );
    return NodeTrackingHelper.convertToTrackingPayload(nodeTrackingsWithTimeSpent, revision.value);
  });

  const sectionTrackings: SectionTracking[] = reactive([]);
  const sectionTrackingsPayload: ComputedRef<SectionTrackingPayload[]> = computed(() =>
    sectionTrackings
      .filter(({ timeSpent }: SectionTracking) => !!timeSpent)
      .map(({ sectionId, timeSpent, editorBlockId }: SectionTracking) => ({
        editorBlockId,
        sectionId,
        timeSpent,
        revision: revision.value,
        type: TrackingType.SectionTimeSpent,
      }))
  );

  let postTrackingStarted = false;
  const hasTrackingsToPost: Ref<boolean> = ref(false);

  watch(hasTrackingsToPost, (newValue: boolean) => {
    if (!newValue || postTrackingStarted) {
      return;
    }
    startPostTracking();
  });

  const resetTimeSpent = () => {
    nodeTrackings.forEach((nodeTracking: NodeTracking) => (nodeTracking.timeSpent = 0));
    sectionTrackings.forEach((sectionTracking: NodeTracking) => (sectionTracking.timeSpent = 0));
    hasTrackingsToPost.value = false;
  };

  const startPostTracking = () => {
    postTrackingStarted = true;
    setTimeout(() => {
      postTracking();
    }, 3000);
  };

  const postTracking = async () => {
    try {
      await TrackingService.trackEditor(sectionTrackingsPayload.value, nodeTrackingsPayload.value);
    } catch (e) {
      console.error('Could not post tracking');
    } finally {
      resetTimeSpent();
      postTrackingStarted = false;
    }
  };

  const postClickTracking = async (tracking: Partial<NodeTrackingPayload>) => {
    const payload = [
      {
        ...tracking,
        revision: revision.value,
        type: TrackingType.NodeClick,
      } as NodeTrackingPayload,
    ];
    try {
      await TrackingService.trackEditor([], payload);
    } catch (e) {
      console.error('Could not post tracking');
    }
  };

  watch(ellapsedTime, () => {
    increaseNodeTimeSpent();
    increaseSectionTimeSpent();
  });

  const increaseNodeTimeSpent = () => {
    nodeTrackings.forEach((nodeTracking: NodeTracking) => {
      if (!nodeTracking.visible) {
        return;
      }
      nodeTracking.timeSpent++;
      hasTrackingsToPost.value = true;
    });
  };

  const increaseSectionTimeSpent = () => {
    sectionTrackings.forEach((sectionTracking: SectionTracking) => {
      if (!sectionTracking.visible) {
        return;
      }
      sectionTracking.timeSpent++;
      hasTrackingsToPost.value = true;
    });
  };

  const observeSectionVisibility = (payload: SectionVisibilityPayload) => {
    trackingStore.setVisibleSections(payload as any);

    if (
      payload.visible &&
      !sectionTrackings.some(
        (sectionTracking: SectionTracking) => sectionTracking.sectionId === payload.sectionId
      )
    ) {
      sectionTrackings.push({
        sectionId: payload.sectionId,
        editorBlockId: payload.editorBlockId,
        visible: true,
        timeSpent: 0,
      });
      return;
    }
    sectionTrackings.forEach((sectionTracking: SectionTracking) => {
      if (sectionTracking.sectionId === payload.sectionId) {
        sectionTracking.visible = payload.visible;
      }
    });
  };

  const observeNodeVisibility = (payload: NodeVisibilityPayload) => {
    if (payload.visible && !NodeTrackingHelper.isIdsInTrackings(payload, nodeTrackings)) {
      nodeTrackings.push(NodeTrackingHelper.convertToTracking(payload));
      return;
    }
    nodeTrackings.forEach((nodeTracking: NodeTracking) => {
      if (NodeTrackingHelper.isSame(nodeTracking, payload)) {
        nodeTracking.visible = payload.visible;
      }
    });
  };

  onMounted(() => {
    stopTimer();
    startTimer();
    window.addEventListener('focus', startTimer);
    window.addEventListener('blur', stopTimer);
  });

  onUnmounted(() => {
    stopTimer();
    window.removeEventListener('focus', startTimer);
    window.addEventListener('blur', stopTimer);
  });

  return {
    observeSectionVisibility,
    observeNodeVisibility,
    postClickTracking,
  };
}
